(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const resetCard = (card) => {
    if (!card) return;
    card.classList.remove('pl-is-tilting');
    card.style.setProperty('--pl-tilt-x', '0deg');
    card.style.setProperty('--pl-tilt-y', '0deg');
  };

  if (!root.dataset.plataneInteractionsReady) {
    root.dataset.plataneInteractionsReady = 'true';

    document.addEventListener('pointermove', (event) => {
      if (reduceMotion.matches || !finePointer.matches) return;
      const card = event.target.closest('.card-wrapper');
      if (!card) return;

      const bounds = card.getBoundingClientRect();
      const x = clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
      const y = clamp((event.clientY - bounds.top) / bounds.height, 0, 1);
      card.classList.add('pl-is-tilting');
      card.style.setProperty('--pl-tilt-x', `${((0.5 - y) * 5).toFixed(2)}deg`);
      card.style.setProperty('--pl-tilt-y', `${((x - 0.5) * 5).toFixed(2)}deg`);
    }, { passive: true });

    document.addEventListener('pointerout', (event) => {
      const card = event.target.closest('.card-wrapper');
      if (!card || card.contains(event.relatedTarget)) return;
      resetCard(card);
    }, { passive: true });
  }

  const revealObserver = reduceMotion.matches || !('IntersectionObserver' in window)
    ? null
    : new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('pl-is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const initReveals = (scope = document) => {
    scope.querySelectorAll('[data-pl-reveal]:not([data-pl-reveal-ready])').forEach((element) => {
      element.dataset.plRevealReady = 'true';
      if (revealObserver) revealObserver.observe(element);
      else element.classList.add('pl-is-visible');
    });
  };

  const initStickyAddToCart = (scope = document) => {
    scope.querySelectorAll('[data-pl-sticky-atc]:not([data-pl-sticky-ready])').forEach((bar) => {
      bar.dataset.plStickyReady = 'true';
      const section = bar.closest('product-info');
      const stickyButton = bar.querySelector('[data-pl-sticky-button]');
      const stickyPrice = bar.querySelector('[data-pl-sticky-price]');
      if (!section || !stickyButton) return;

      const getMainButton = () => section.querySelector('.product-form__submit');
      const getMainPrice = () => section.querySelector('[id^="price-"] .price-item--regular, [id^="price-"] .price-item--sale');

      const sync = () => {
        const mainButton = getMainButton();
        const mainPrice = getMainPrice();
        if (!mainButton) return;

        const nextDisabled = mainButton.disabled;
        const nextLabel = mainButton.querySelector('span')?.textContent?.trim() || mainButton.textContent.trim();
        const nextPrice = mainPrice?.textContent?.trim();

        if (stickyButton.disabled !== nextDisabled) stickyButton.disabled = nextDisabled;
        if (nextLabel && stickyButton.textContent.trim() !== nextLabel) stickyButton.textContent = nextLabel;
        if (stickyPrice && nextPrice && stickyPrice.textContent.trim() !== nextPrice) stickyPrice.textContent = nextPrice;
      };

      let observedMainButton = null;
      const buttonObserver = new MutationObserver(sync);
      let visibilityObserver = null;
      if ('IntersectionObserver' in window) {
        visibilityObserver = new IntersectionObserver(([entry]) => {
          const pastProductStart = section.getBoundingClientRect().top < 0;
          const shouldShow = !entry.isIntersecting && pastProductStart;
          bar.classList.toggle('pl-is-visible', shouldShow);
          bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
          stickyButton.tabIndex = shouldShow ? 0 : -1;
        }, { threshold: 0 });
      }

      const observeMainButton = () => {
        const mainButton = getMainButton();
        if (!mainButton || mainButton === observedMainButton) return mainButton;

        observedMainButton = mainButton;
        buttonObserver.disconnect();
        buttonObserver.observe(mainButton, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        if (visibilityObserver) {
          visibilityObserver.disconnect();
          visibilityObserver.observe(mainButton);
        }

        return mainButton;
      };

      const syncAndObserve = () => {
        observeMainButton();
        sync();
      };

      if (!observeMainButton()) return;
      sync();

      let unsubscribeVariantChange = null;
      if (
        typeof subscribe === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.variantChange
      ) {
        unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (data?.sectionId && String(data.sectionId) !== String(section.dataset.section)) return;
          window.requestAnimationFrame(syncAndObserve);
        });
      }

      stickyButton.addEventListener('click', () => {
        const currentMainButton = getMainButton();
        if (!currentMainButton || currentMainButton.disabled) return;
        stickyButton.disabled = true;
        currentMainButton.click();
        window.setTimeout(syncAndObserve, 900);
      });

      const handleSectionUnload = (event) => {
        if (!event.target.contains(section)) return;
        buttonObserver.disconnect();
        visibilityObserver?.disconnect();
        unsubscribeVariantChange?.();
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
      };

      document.addEventListener('shopify:section:unload', handleSectionUnload);
    });
  };

  const init = (scope = document) => {
    initReveals(scope);
    initStickyAddToCart(scope);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
